import { useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useGeofenceZonesList, GEOFENCE_ZONES_DEFAULT_PAGE_SIZE } from "./useGeofenceZonesList.js";
import {
  getGeofenceZoneDetail,
  createGeofenceZone,
  updateGeofenceZone,
  assignWorkersToZone,
  removeWorkerZoneAssignment,
  evaluateGeofence,
  ZONE_TYPE_CIRCLE,
  ZONE_TYPE_COUNTRY,
  COUNTRY_ZONE_OPTIONS,
} from "./geofenceApi.js";
import "./geofenceZones.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton, { DetailSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Geofence", "Zones"];
const DRAWER_TITLE_ID = "gfz-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;

const ZONE_STATUS_OPTIONS = ["Active", "Inactive"];

const SUBTITLE_TEXT =
  "กำหนดพื้นที่ที่แรงงานควรอยู่ และตรวจว่าใครอยู่นอกพื้นที่จากตำแหน่งล่าสุด";

/**
 * P161: these lines used to say "รองรับโซนรูปวงกลมเท่านั้น", which stopped being
 * true the moment a Country zone could exist - and it was being printed directly
 * above one. They also named WmxWorkerLatestStatus, a table, at an operator.
 *
 * What survives is what an operator can act on: which kinds of zone there are,
 * that a country zone needs no per-worker setup, and that no notification is
 * actually delivered yet - that last one is a real limitation they would
 * otherwise discover by waiting for an alert that never arrives.
 */
const POLICY_LINES = [
  "โซนมี 2 แบบ — วงกลมรอบสถานที่ และทั้งประเทศ",
  "โซนแบบประเทศครอบคลุมแรงงานทุกคนอัตโนมัติ ไม่ต้องเพิ่มแรงงานเข้าโซนทีละคน",
  "การตรวจใช้ตำแหน่งล่าสุดที่บันทึกไว้ของแรงงาน",
  "เมื่อพบว่าอยู่นอกพื้นที่ ระบบจะบันทึกเป็นเหตุผิดปกติไว้ แต่ยังไม่ส่งการแจ้งเตือนออกไปจริง",
];

const POLYGON_RESERVED_NOTE = "โซนรูปอิสระที่วาดเองยังไม่เปิดใช้งาน";

/**
 * P161: what a Country zone shows where a Circle shows a centre and a radius.
 * A country has neither, and printing "13.039160, 101.489260 · รัศมี 0" for one
 * is worse than printing nothing - it looks like a broken circle rather than a
 * different kind of zone.
 */
const COUNTRY_GEOMETRY_TEXT = "ทั้งประเทศ";
const COUNTRY_AUTO_COVER_TEXT = "ทุกคนอัตโนมัติ";
const CIRCLE_VIZ_CAPTION = "ภาพจำลอง Circle zone ไม่ใช่แผนที่จริง (สร้างจากพิกัดที่บันทึกไว้เพื่อการอ้างอิงเท่านั้น)";

const CREATE_VIEW_ONLY_NOTICE =
  "SuperAgent/PLC เป็นแบบดูอย่างเดียวตามนโยบาย (view-only) — สร้าง/แก้ไขได้เฉพาะ SuperAdmin หรือ Agent เจ้าของโซนเท่านั้น";
const NO_POLYGON_FIELD_NOTICE = "รองรับเฉพาะโซนรูปวงกลม";

/**
 * P161: what choosing the country profile actually means, said before the
 * operator commits to it. Two facts they cannot see anywhere else on this
 * screen: it covers everyone with no assigning, and it is the leaving-the-
 * country alert.
 */
const COUNTRY_PROFILE_NOTICE =
  "โซนแบบประเทศจะครอบคลุมแรงงานทุกคนโดยอัตโนมัติ ไม่ต้องเพิ่มแรงงานเข้าโซนทีละคน และจะแจ้งเตือนเมื่อตำแหน่งล่าสุดของแรงงานอยู่นอกประเทศที่เลือก";

const COUNTRY_PROFILE_SUPERADMIN_NOTICE = "โซนแบบประเทศสร้างได้เฉพาะผู้ดูแลระบบเท่านั้น";
const RADIUS_HINT_TEXT = "ละติจูด/ลองจิจูดต้องอยู่ในช่วงที่ถูกต้อง (-90 ถึง 90 / -180 ถึง 180) และรัศมีต้องอยู่ระหว่าง 10-5000 เมตร";

const ASSIGN_NOTICE_LINES = [
  "มอบหมายได้สูงสุด 200 แรงงานต่อคำขอ",
  "แรงงานที่มี assignment แบบ Active อยู่แล้วจะถูกข้ามอัตโนมัติ (idempotent)",
  "แรงงานที่ไม่ได้อยู่ในสายงานของ Agent จะถูกปฏิเสธ",
];
const ASSIGN_NO_NOTIFICATION_NOTICE = "ไม่มีการส่ง notification ในขั้นตอนนี้";
const MAX_WORKER_ASSIGNMENT_BATCH = 200;

const EVALUATE_DRYRUN_WARNING = "dryRun จะไม่สร้าง OutOfZone abnormal";
const EVALUATE_SOURCE_WARNING = "Evaluation อ่าน WmxWorkerLatestStatus เท่านั้น";
const NO_FAKE_LOCATION_SCORE_NOTE = "ไม่มีการให้คะแนนความน่าเชื่อถือของตำแหน่ง (fake-location score)";

const FOOTNOTE_TEXT =
  "โซนแบบวงกลมกำหนดจากจุดกึ่งกลางและรัศมี ส่วนโซนแบบประเทศใช้ขอบเขตประเทศจริง การตรวจแต่ละครั้งจะบันทึกไว้ให้ย้อนดูได้";

const ZONE_STATUS_BADGE_VARIANT = { Active: "success", Inactive: "neutral" };
const EVAL_STATUS_BADGE_VARIANT = { Inside: "success", Outside: "danger", NoLocation: "neutral" };

/** P161: true for a zone whose geometry is a whole country rather than a circle. */
function isCountryZone(zone) {
  return zone?.zoneType === ZONE_TYPE_COUNTRY;
}

/** P161: the zone kind, in words an operator reads - never the stored enum. */
function zoneTypeLabel(zone) {
  return isCountryZone(zone) ? "ทั้งประเทศ" : "วงกลม";
}

/**
 * P161: what covers this zone. A country zone covers everyone with no
 * assignment rows at all, so its stored assignedWorkerCount is always 0 -
 * printing that 0 would read as "this fence is protecting nobody", which is the
 * exact opposite of what it does.
 */
function zoneCoverageText(zone) {
  return zone?.autoAssignAllWorkers ? COUNTRY_AUTO_COVER_TEXT : String(zone?.assignedWorkerCount ?? 0);
}

function zoneStatusBadgeVariant(value) {
  return ZONE_STATUS_BADGE_VARIANT[value] ?? "neutral";
}

function evalStatusBadgeVariant(value) {
  return EVAL_STATUS_BADGE_VARIANT[value] ?? "neutral";
}

function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

function formatCoordinate(value) {
  return typeof value === "number" ? value.toFixed(6) : "-";
}

function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการ Geofence Zone นี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียด Zone นี้";
  }
  if (error?.status === 404) {
    return "ไม่พบ Zone นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

function describeWriteError(error, conflictFallback) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำรายการนี้ เฉพาะ SuperAdmin หรือ Agent เจ้าของโซนเท่านั้น (SuperAgent/PLC เป็นแบบดูอย่างเดียว)";
  }
  if (error?.status === 404) {
    return "ไม่พบ Zone นี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 409) {
    return conflictFallback;
  }
  if (error?.status === 400) {
    return "กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง";
  }
  return "ไม่สามารถบันทึกรายการนี้ได้ กรุณาลองใหม่อีกครั้ง";
}

function describeEvaluateError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เรียกใช้การประเมินนี้";
  }
  return "ไม่สามารถเรียกใช้การประเมินได้ กรุณาลองใหม่อีกครั้ง";
}

function validateLatLngRadius({ centerLatitude, centerLongitude, radiusMeters }) {
  const lat = Number(centerLatitude);
  const lng = Number(centerLongitude);
  const radius = Number(radiusMeters);

  if (centerLatitude === "" || Number.isNaN(lat) || lat < -90 || lat > 90) {
    return "centerLatitude ต้องเป็นตัวเลขระหว่าง -90 ถึง 90";
  }
  if (centerLongitude === "" || Number.isNaN(lng) || lng < -180 || lng > 180) {
    return "centerLongitude ต้องเป็นตัวเลขระหว่าง -180 ถึง 180";
  }
  if (radiusMeters === "" || Number.isNaN(radius) || radius < 10 || radius > 5000) {
    return "radiusMeters ต้องเป็นตัวเลขระหว่าง 10 ถึง 5000 เมตร";
  }
  return null;
}

function parseWorkerIdsInput(rawText) {
  return Array.from(
    new Set(
      String(rawText || "")
        .split(/[\n,]/)
        .map((value) => value.trim())
        .filter(Boolean)
    )
  );
}

// P161: zoneType/countryCode join the form. Circle stays the default, so the
// screen opens on exactly the form it always opened on.
const EMPTY_CREATE_FORM = {
  zoneName: "",
  zoneType: ZONE_TYPE_CIRCLE,
  countryCode: COUNTRY_ZONE_OPTIONS[0]?.countryCode ?? "",
  employerId: "",
  centerLatitude: "",
  centerLongitude: "",
  radiusMeters: "",
  note: "",
};

/**
 * /backoffice/geofence/zones - the Backoffice Geofence Zones screen (Prompt
 * 119B, built from the W57 reference). Entirely over the Prompt 118
 * Backoffice Geofence Zone API: a paginated, scope-isolated list of Circle
 * zones (GET .../geofence/zones), a single-zone detail with an assigned-
 * worker summary (GET .../geofence/zones/{zoneId}), Create Zone
 * (POST .../geofence/zones), Edit Zone (PATCH .../geofence/zones/{zoneId}),
 * Assign Workers (POST .../geofence/zones/{zoneId}/worker-assignments),
 * worker-zone removal (POST .../worker-assignments/{workerId}/remove), and a
 * manual evaluation of the latest known worker location against active zones
 * (POST /api/backoffice/geofence/evaluate). This screen never calls a
 * polygon/fake-location-scoring/face-recognition/SOS API (none of those
 * exist anywhere in this codebase), never scans location event history (the
 * backend's own evaluation reads dbo.WmxWorkerLatestStatus only), never
 * renders a real map (the Circle visualization is a static SVG illustration
 * built only from a zone's own centerLatitude/centerLongitude/radiusMeters),
 * and never sends a real notification - notificationDeliveryStarted is
 * always rendered false with the backend's own fixed reserved-reason string.
 */
export default function GeofenceZonesPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [zoneStatusFilter, setZoneStatusFilter] = useState("");
  const [superAgentOrgInput, setSuperAgentOrgInput] = useState("");
  const [plcOrgInput, setPlcOrgInput] = useState("");
  const [agentIdInput, setAgentIdInput] = useState("");
  const [employerIdInput, setEmployerIdInput] = useState("");
  const [page, setPage] = useState(1);

  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [drawerView, setDrawerView] = useState(null); // null | "detail" | "create" | "edit" | "assign" | "evaluate" | "outofzone"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [createValidationError, setCreateValidationError] = useState(null);
  const [createSubmit, setCreateSubmit] = useState({ status: "idle", error: null });

  const [editForm, setEditForm] = useState(null);
  const [editValidationError, setEditValidationError] = useState(null);
  const [editSubmit, setEditSubmit] = useState({ status: "idle", error: null });

  const [assignWorkerIdsInput, setAssignWorkerIdsInput] = useState("");
  const [assignmentNoteInput, setAssignmentNoteInput] = useState("");
  const [assignValidationError, setAssignValidationError] = useState(null);
  const [assignSubmit, setAssignSubmit] = useState({ status: "idle", error: null, result: null });
  const [removeWorkerState, setRemoveWorkerState] = useState({ status: "idle", workerId: null, error: null });

  const [evaluateWorkerIdInput, setEvaluateWorkerIdInput] = useState("");
  const [evaluateZoneIdInput, setEvaluateZoneIdInput] = useState("");
  const [evaluateDryRun, setEvaluateDryRun] = useState(true);
  const [evaluateSubmit, setEvaluateSubmit] = useState({ status: "idle", error: null, result: null });
  const [selectedResultIndex, setSelectedResultIndex] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, zoneStatusFilter, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: GEOFENCE_ZONES_DEFAULT_PAGE_SIZE,
      search,
      zoneStatus: zoneStatusFilter,
      superAgentOrganizationId: superAgentOrgInput,
      plcOrganizationId: plcOrgInput,
      agentId: agentIdInput,
      employerId: employerIdInput,
    }),
    [page, search, zoneStatusFilter, superAgentOrgInput, plcOrgInput, agentIdInput, employerIdInput]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useGeofenceZonesList(filters);

  // Detail is fetched whenever selectedZoneId changes (row/card opened) -
  // edit/assign subviews reuse the same fetch, they never issue a second GET
  // just to switch views within the same open drawer.
  useEffect(() => {
    if (!selectedZoneId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedZoneId ? previous.data : null,
      error: null,
      forId: selectedZoneId,
    }));

    getGeofenceZoneDetail(selectedZoneId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedZoneId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedZoneId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedZoneId
  }, [selectedZoneId, detailReloadToken]);

  function openDetail(zoneId) {
    setSelectedZoneId(zoneId);
    setDrawerView("detail");
    setEditForm(null);
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
    setAssignWorkerIdsInput("");
    setAssignmentNoteInput("");
    setAssignValidationError(null);
    setAssignSubmit({ status: "idle", error: null, result: null });
    setRemoveWorkerState({ status: "idle", workerId: null, error: null });
  }

  function openCreate() {
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateValidationError(null);
    setCreateSubmit({ status: "idle", error: null });
    setDrawerView("create");
  }

  function openEdit() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setEditForm({
      zoneName: detail.zoneName ?? "",
      centerLatitude: detail.centerLatitude ?? "",
      centerLongitude: detail.centerLongitude ?? "",
      radiusMeters: detail.radiusMeters ?? "",
      zoneStatus: detail.zoneStatus ?? "Active",
      note: detail.note ?? "",
    });
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
    setDrawerView("edit");
  }

  function openAssign() {
    setAssignWorkerIdsInput("");
    setAssignmentNoteInput("");
    setAssignValidationError(null);
    setAssignSubmit({ status: "idle", error: null, result: null });
    setDrawerView("assign");
  }

  function openEvaluate(prefillZoneId) {
    setEvaluateWorkerIdInput("");
    setEvaluateZoneIdInput(prefillZoneId || "");
    setEvaluateDryRun(true);
    setEvaluateSubmit({ status: "idle", error: null, result: null });
    setSelectedResultIndex(null);
    setDrawerView("evaluate");
  }

  function openOutOfZone(index) {
    setSelectedResultIndex(index);
    setDrawerView("outofzone");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedZoneId(null);
  }

  function handleRefresh() {
    refetchList();
    if (selectedZoneId) {
      setDetailReloadToken((token) => token + 1);
    }
  }

  function handleCreateSubmit() {
    const trimmedName = createForm.zoneName.trim();
    if (!trimmedName) {
      setCreateValidationError("กรุณาระบุ zoneName");
      return;
    }
    // P161: a country zone has no centre and no radius, so the circle range
    // checks simply do not apply to it - it needs a country instead.
    if (createForm.zoneType === ZONE_TYPE_COUNTRY) {
      if (!createForm.countryCode) {
        setCreateValidationError("กรุณาเลือกประเทศ");
        return;
      }
    } else {
      const rangeError = validateLatLngRadius(createForm);
      if (rangeError) {
        setCreateValidationError(rangeError);
        return;
      }
    }
    setCreateValidationError(null);
    setCreateSubmit({ status: "submitting", error: null });
    createGeofenceZone({ ...createForm, zoneName: trimmedName })
      .then(() => {
        setCreateSubmit({ status: "success", error: null });
        refetchList();
        closeDrawer();
      })
      .catch((error) => {
        setCreateSubmit({ status: "error", error });
      });
  }

  function handleEditSubmit() {
    if (!editForm) {
      return;
    }
    const trimmedName = editForm.zoneName.trim();
    if (!trimmedName) {
      setEditValidationError("zoneName ไม่สามารถเว้นว่างได้");
      return;
    }
    const rangeError = validateLatLngRadius(editForm);
    if (rangeError) {
      setEditValidationError(rangeError);
      return;
    }
    setEditValidationError(null);
    setEditSubmit({ status: "submitting", error: null });
    updateGeofenceZone(selectedZoneId, { ...editForm, zoneName: trimmedName })
      .then(() => {
        setEditSubmit({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        setDrawerView("detail");
      })
      .catch((error) => {
        setEditSubmit({ status: "error", error });
      });
  }

  function handleAssignSubmit() {
    const workerIds = parseWorkerIdsInput(assignWorkerIdsInput);
    if (workerIds.length === 0) {
      setAssignValidationError("กรุณาระบุ workerIds อย่างน้อย 1 รายการ");
      return;
    }
    if (workerIds.length > MAX_WORKER_ASSIGNMENT_BATCH) {
      setAssignValidationError(`มอบหมายได้สูงสุด ${MAX_WORKER_ASSIGNMENT_BATCH} รายการต่อคำขอ`);
      return;
    }
    setAssignValidationError(null);
    setAssignSubmit({ status: "submitting", error: null, result: null });
    assignWorkersToZone(selectedZoneId, { workerIds, assignmentNote: assignmentNoteInput })
      .then((result) => {
        setAssignSubmit({ status: "success", error: null, result });
        setDetailReloadToken((token) => token + 1);
        refetchList();
      })
      .catch((error) => {
        setAssignSubmit((previous) => ({ status: "error", error, result: previous.result }));
      });
  }

  function handleRemoveWorker(workerId) {
    setRemoveWorkerState({ status: "submitting", workerId, error: null });
    removeWorkerZoneAssignment(selectedZoneId, workerId)
      .then(() => {
        setRemoveWorkerState({ status: "success", workerId, error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
      })
      .catch((error) => {
        setRemoveWorkerState({ status: "error", workerId, error });
      });
  }

  function handleEvaluateSubmit() {
    setEvaluateSubmit((previous) => ({ status: "submitting", error: null, result: previous.result }));
    setSelectedResultIndex(null);
    evaluateGeofence({
      workerId: evaluateWorkerIdInput || undefined,
      zoneId: evaluateZoneIdInput || undefined,
      dryRun: evaluateDryRun,
    })
      .then((result) => {
        setEvaluateSubmit({ status: "success", error: null, result });
        // A dryRun result never fakes created abnormals - only a real
        // createdAbnormalCount > 0 (which a dryRun response never has, per
        // the backend's own contract) is worth refetching the list for.
        if (result?.createdAbnormalCount > 0) {
          refetchList();
        }
      })
      .catch((error) => {
        setEvaluateSubmit((previous) => ({ status: "error", error, result: previous.result }));
      });
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / GEOFENCE_ZONES_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(search || zoneStatusFilter || superAgentOrgInput || plcOrgInput || agentIdInput || employerIdInput);

  const isInitialLoading = listStatus === "loading" && listData === null;
  const isInitialError = listStatus === "error" && listData === null;

  if (isInitialLoading) {
    return (
      <AppShell crumbs={CRUMBS}>
        <PageSkeleton variant="map" label="กำลังโหลดรายการ Geofence Zone..." />
      </AppShell>
    );
  }

  if (isInitialError) {
    return (
      <AppShell crumbs={CRUMBS}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายการ Geofence Zone ได้</h1>
          <p className="wx-state-body">{describeListError(listError)}</p>
          <button type="button" className="wx-button wx-button--primary" onClick={handleRefresh}>
            ลองใหม่อีกครั้ง
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-gfz-page">
        <header className="wx-gfz-head">
          <div className="wx-gfz-title">
            <h1>
              Geofence Zones <span className="wx-gfz-title-th">พื้นที่อนุญาต</span>
            </h1>
            <p>{SUBTITLE_TEXT}</p>
          </div>
          {primaryRole && (
            <span className="wx-gfz-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-gfz-policy-banner" role="status">
          <div className="wx-gfz-policy-banner-title">
            <span className="wx-gfz-policy-dot" aria-hidden="true" />
            นโยบาย Geofence Zone
          </div>
          <ul>
            {POLICY_LINES.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>

        {/* A row of five KPI tiles stood here, every one of them rendering the
            word "สงวนไว้" over a pill reading "รอ Summary API" - a whole band of
            the page, on every visit, reporting that we had not built a summary
            endpoint. No number was ever shown. The list below is the real
            content; it is now the first thing under the header. */}

        <div className="wx-gfz-toolbar">
          <label className="wx-gfz-search">
            <span className="wx-visually-hidden">ค้นหารหัส Zone หรือชื่อ Zone</span>
            <input type="search" placeholder="ค้นหารหัส Zone หรือชื่อ Zone" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
          </label>

          <div className="wx-gfz-toolbar-actions">
            <button type="button" className="wx-button wx-button--ghost wx-gfz-refresh-button" onClick={handleRefresh}>
              Refresh
            </button>
            <button type="button" className="wx-button wx-button--ghost wx-gfz-evaluate-button" onClick={() => openEvaluate("")}>
              ประเมิน Zone (Manual Evaluate)
            </button>
            <button type="button" className="wx-button wx-button--primary wx-gfz-create-button" onClick={openCreate}>
              + Create Zone
            </button>
          </div>

          <div className="wx-gfz-filters">
            <label className="wx-gfz-filter">
              <span>Zone Status</span>
              <select value={zoneStatusFilter} onChange={(event) => setZoneStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {ZONE_STATUS_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label className="wx-gfz-filter">
              <span>Super Agent Org</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={superAgentOrgInput} onChange={(event) => setSuperAgentOrgInput(event.target.value)} />
            </label>

            <label className="wx-gfz-filter">
              <span>PLC / บมจ</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={plcOrgInput} onChange={(event) => setPlcOrgInput(event.target.value)} />
            </label>

            <label className="wx-gfz-filter">
              <span>Agent</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={agentIdInput} onChange={(event) => setAgentIdInput(event.target.value)} />
            </label>

            <label className="wx-gfz-filter">
              <span>นายจ้าง</span>
              <input type="text" placeholder="รหัส (ไม่บังคับ)" value={employerIdInput} onChange={(event) => setEmployerIdInput(event.target.value)} />
            </label>

          </div>
        </div>

        <section className="wx-panel wx-gfz-list-panel">
          <div className="wx-gfz-list-head">
            <h2 className="wx-panel-title">รายการ Geofence Zone</h2>
            <span className="wx-gfz-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-gfz-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบรายการที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มี Geofence Zone ในขอบเขตที่มองเห็น"}</p>
          ) : (
            <>
              <div className="wx-gfz-table-wrap">
                <table className="wx-gfz-table">
                  <colgroup>
                    {/* ELEVEN cols for eleven columns, summing to 100.
                        There were TWELVE, totalling 107%. table-layout:fixed
                        reads them in order and ignores the extra, so the real
                        columns were sized off a list that neither matched them
                        nor added up - which is what made this header grid look
                        wrong. AbnormalHeartbeatPage hit the identical bug; if a
                        column is added or removed here, change this list too. */}
                    <col style={{ width: "10%" }} /><col style={{ width: "13%" }} /><col style={{ width: "7%" }} /><col style={{ width: "7%" }} />
                    <col style={{ width: "12%" }} /><col style={{ width: "7%" }} /><col style={{ width: "11%" }} /><col style={{ width: "9%" }} />
                    <col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Zone Code</th>
                      <th>ชื่อ Zone</th>
                      <th>Status</th>
                      <th>ประเภท</th>
                      <th>พิกัดศูนย์กลาง</th>
                      <th>รัศมี (ม.)</th>
                      <th>นายจ้าง</th>
                      <th>Agent</th>
                      <th>แรงงานที่มอบหมาย</th>
                      <th>สร้างเมื่อ (UTC)</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.zoneId} className="wx-gfz-row" onClick={() => openDetail(item.zoneId)}>
                        <td>
                          <span className="wx-gfz-code">{item.zoneCode ?? "-"}</span>
                        </td>
                        <td>
                          <strong className="wx-gfz-cell-strong">{item.zoneName ?? "-"}</strong>
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${zoneStatusBadgeVariant(item.zoneStatus)}`}>{item.zoneStatus}</span>
                        </td>
                        <td>
                          <span className="wx-badge wx-badge--info">{zoneTypeLabel(item)}</span>
                        </td>
                        {/* A country zone has no centre and no radius. It says
                            what it covers instead of printing a coordinate pair
                            and a 0 that would read as a broken circle. */}
                        {isCountryZone(item) ? (
                          <td colSpan={2}>
                            <span className="wx-gfz-cell-strong">
                              {COUNTRY_GEOMETRY_TEXT}
                              {item.countryCode ? ` (${item.countryCode})` : ""}
                            </span>
                          </td>
                        ) : (
                          <>
                            <td>
                              {formatCoordinate(item.centerLatitude)}, {formatCoordinate(item.centerLongitude)}
                            </td>
                            <td>{item.radiusMeters ?? "-"}</td>
                          </>
                        )}
                        <td>
                          <span className="wx-gfz-cell-strong">{item.employerName ?? (isCountryZone(item) ? "-" : "ไม่ระบุ")}</span>
                        </td>
                        <td>
                          <span className="wx-gfz-cell-strong">{item.agentName ?? "-"}</span>
                        </td>
                        <td>{zoneCoverageText(item)}</td>
                        <td>
                          <span className="wx-gfz-cell-strong">{formatDateTimeTh(item.createdAtUtc) ?? "-"}</span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="wx-gfz-row-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetail(item.zoneId);
                            }}
                          >
                            เปิดรายละเอียด
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-gfz-cards">
                {items.map((item) => (
                  <ZoneCard key={item.zoneId} item={item} onOpen={() => openDetail(item.zoneId)} />
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>

        <p className="wx-gfz-footnote">{FOOTNOTE_TEXT}</p>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-gfz-row, .wx-gfz-card, .wx-gfz-refresh-button, .wx-gfz-evaluate-button, .wx-gfz-create-button"
      >
        {drawerView === "detail" && (
          <ZoneDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onEdit={openEdit}
            onAssign={openAssign}
            onEvaluate={() => openEvaluate(selectedZoneId)}
            onRemoveWorker={handleRemoveWorker}
            removeWorkerState={removeWorkerState}
          />
        )}
        {drawerView === "create" && (
          <CreateZoneView
            titleId={DRAWER_TITLE_ID}
            form={createForm}
            onChange={(patch) => setCreateForm((previous) => ({ ...previous, ...patch }))}
            validationError={createValidationError}
            submitState={createSubmit}
            onSubmit={handleCreateSubmit}
          />
        )}
        {drawerView === "edit" && editForm && (
          <EditZoneView
            titleId={DRAWER_TITLE_ID}
            zoneCode={detailState.data?.zoneCode}
            form={editForm}
            onChange={(patch) => setEditForm((previous) => ({ ...previous, ...patch }))}
            validationError={editValidationError}
            submitState={editSubmit}
            onSubmit={handleEditSubmit}
            onBack={() => setDrawerView("detail")}
          />
        )}
        {drawerView === "assign" && (
          <AssignWorkersView
            titleId={DRAWER_TITLE_ID}
            zoneCode={detailState.data?.zoneCode}
            workerIdsInput={assignWorkerIdsInput}
            onWorkerIdsChange={setAssignWorkerIdsInput}
            assignmentNote={assignmentNoteInput}
            onAssignmentNoteChange={setAssignmentNoteInput}
            validationError={assignValidationError}
            submitState={assignSubmit}
            onSubmit={handleAssignSubmit}
          />
        )}
        {drawerView === "evaluate" && (
          <EvaluateView
            titleId={DRAWER_TITLE_ID}
            workerIdInput={evaluateWorkerIdInput}
            onWorkerIdChange={setEvaluateWorkerIdInput}
            zoneIdInput={evaluateZoneIdInput}
            onZoneIdChange={setEvaluateZoneIdInput}
            dryRun={evaluateDryRun}
            onDryRunChange={setEvaluateDryRun}
            submitState={evaluateSubmit}
            onSubmit={handleEvaluateSubmit}
            onOpenOutOfZone={openOutOfZone}
          />
        )}
        {drawerView === "outofzone" && selectedResultIndex !== null && evaluateSubmit.result && (
          <OutOfZoneResultView
            titleId={DRAWER_TITLE_ID}
            evaluateResponse={evaluateSubmit.result}
            item={evaluateSubmit.result.results[selectedResultIndex]}
            onBack={() => setDrawerView("evaluate")}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

function ZoneCard({ item, onOpen }) {
  return (
    <div className="wx-gfz-card" onClick={onOpen}>
      <div className="wx-gfz-card-top">
        <div>
          <strong>{item.zoneCode ?? "-"}</strong>
          <span className="wx-gfz-card-sub">{item.zoneName ?? "-"}</span>
        </div>
        <div className="wx-gfz-card-tags">
          <span className={`wx-badge wx-badge--${zoneStatusBadgeVariant(item.zoneStatus)}`}>{item.zoneStatus}</span>
        </div>
      </div>
      <div className="wx-gfz-card-grid">
        {/* Same rule as the table: a country zone reports what it covers, not a
            radius it does not have. */}
        <div>
          <label>{isCountryZone(item) ? "ขอบเขต" : "รัศมี"}</label>
          <strong>{isCountryZone(item) ? COUNTRY_GEOMETRY_TEXT : `${item.radiusMeters ?? "-"} ม.`}</strong>
        </div>
        <div>
          <label>แรงงานที่มอบหมาย</label>
          <strong>{zoneCoverageText(item)}</strong>
        </div>
      </div>
      <button
        type="button"
        className="wx-gfz-row-action wx-gfz-row-action--full"
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
      >
        เปิดรายละเอียด
      </button>
    </div>
  );
}

/** Static SVG illustration built only from a zone's own radiusMeters - no map tile, no external library, no per-worker geo data (ZoneAssignedWorkerSummaryDto has none). */
function CircleZoneViz({ radiusMeters }) {
  return (
    <div className="wx-gfz-circle-viz">
      <svg viewBox="0 0 300 190" role="img" aria-label="ภาพจำลอง Circle zone จากรัศมีที่บันทึกไว้">
        <circle cx="150" cy="95" r="70" fill="var(--wx-color-primary-100)" stroke="var(--wx-color-primary-600)" strokeWidth="2" strokeDasharray="5 4" />
        <line x1="150" y1="95" x2="220" y2="95" stroke="var(--wx-color-primary-600)" strokeWidth="1" strokeDasharray="3 3" />
        <text x="182" y="88" fontSize="9" fill="var(--wx-color-primary-700)" fontWeight="700">
          {radiusMeters ?? "-"} ม.
        </text>
        <circle cx="150" cy="95" r="3" fill="var(--wx-color-text)" />
      </svg>
      <p className="wx-gfz-circle-viz-caption">{CIRCLE_VIZ_CAPTION}</p>
    </div>
  );
}

function ZoneDetailView({ titleId, state, onEdit, onAssign, onEvaluate, onRemoveWorker, removeWorkerState }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดรายละเอียด..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-gfz-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดรายละเอียดได้
        </h2>
        <p className="wx-state-body">{describeDetailError(state.error)}</p>
      </div>
    );
  }

  const detail = state.data;
  const nextActions = detail.nextActions ?? {};
  const assignedWorkers = detail.assignedWorkers ?? [];

  return (
    <div className="wx-gfz-drawer">
      <span className="wx-gfz-drawer-kicker">GEOFENCE ZONE · DETAIL</span>
      <h2 id={titleId} className="wx-gfz-drawer-title">
        รายละเอียด Geofence Zone
      </h2>
      <p className="wx-gfz-drawer-sub">
        {detail.zoneCode ?? "-"} · {detail.zoneName ?? "-"}
      </p>
      <div className="wx-gfz-drawer-status">
        <span className={`wx-badge wx-badge--${zoneStatusBadgeVariant(detail.zoneStatus)}`}>{detail.zoneStatus}</span>
        <span className="wx-badge wx-badge--info">{zoneTypeLabel(detail)}</span>
        <span className="wx-badge wx-badge--neutral">{detail.zoneCode ?? "-"}</span>
      </div>

      {/* P161: a country zone has no centre, no radius and no circle to draw.
          It gets its own panel rather than the circle one filled with zeros -
          "13.039160, 101.489260 · รัศมี 0" describes nothing an operator can
          use, and reads as a broken circle rather than a different kind of
          zone. */}
      {isCountryZone(detail) ? (
        <section className="wx-gfz-dsec">
          <h3>ขอบเขตโซน</h3>
          <div className="wx-gfz-geometry-grid">
            <div className="wx-gfz-geometry-cell">
              <span>ประเภทโซน</span>
              <strong>{zoneTypeLabel(detail)}</strong>
            </div>
            <div className="wx-gfz-geometry-cell">
              <span>ประเทศ</span>
              <strong>{detail.countryName ?? detail.countryCode ?? "-"}</strong>
            </div>
          </div>
          <p className="wx-gfz-field-hint">
            ใช้ขอบเขตประเทศจริงในการตรวจ ไม่ได้ใช้จุดกึ่งกลางและรัศมี — แรงงานทุกคนอยู่ในโซนนี้โดยอัตโนมัติ
            และจะแจ้งเตือนเมื่อตำแหน่งล่าสุดอยู่นอกประเทศ
          </p>
        </section>
      ) : (
        <section className="wx-gfz-dsec">
          <h3>รูปทรง Circle</h3>
          <div className="wx-gfz-geometry-grid">
            <div className="wx-gfz-geometry-cell">
              <span>ละติจูดจุดกึ่งกลาง</span>
              <strong>{formatCoordinate(detail.centerLatitude)}</strong>
            </div>
            <div className="wx-gfz-geometry-cell">
              <span>ลองจิจูดจุดกึ่งกลาง</span>
              <strong>{formatCoordinate(detail.centerLongitude)}</strong>
            </div>
            <div className="wx-gfz-geometry-cell">
              <span>รัศมี (เมตร)</span>
              <strong>{detail.radiusMeters ?? "-"}</strong>
            </div>
            <div className="wx-gfz-geometry-cell">
              <span>ประเภทโซน</span>
              <strong>{zoneTypeLabel(detail)}</strong>
            </div>
          </div>

          <CircleZoneViz radiusMeters={detail.radiusMeters} />

          <div className="wx-gfz-reserved-note">{POLYGON_RESERVED_NOTE}</div>
        </section>
      )}

      <section className="wx-gfz-dsec">
        <h3>นายจ้าง / Agent</h3>
        <div className="wx-gfz-kv-row">
          <span>นายจ้าง</span>
          {/* A country zone belongs to no employer by design, so "ไม่ระบุ" -
              which reads as "somebody forgot to fill this in" - would be wrong. */}
          <strong>{detail.employerName ?? (isCountryZone(detail) ? "-" : "ไม่ระบุ")}</strong>
        </div>
        <div className="wx-gfz-kv-row">
          <span>Agent</span>
          <strong>{detail.agentName ?? "-"}</strong>
        </div>
        <div className="wx-gfz-kv-row">
          <span>สร้างเมื่อ</span>
          <strong>{formatDateTimeTh(detail.createdAtUtc) ?? "-"}</strong>
        </div>
        {detail.note && (
          <div className="wx-gfz-kv-row">
            <span>หมายเหตุ</span>
            <strong>{detail.note}</strong>
          </div>
        )}
      </section>

      <section className="wx-gfz-dsec">
        <h3>แรงงานที่อยู่ในโซนนี้ ({zoneCoverageText(detail)})</h3>
        {/* P161: an auto-covering zone has no assignment rows BY DESIGN, so the
            ordinary empty state ("ยังไม่มีแรงงานที่มอบหมายให้ Zone นี้") would
            flatly contradict the heading right above it and read as a fence
            protecting nobody. It gets the explanation instead. */}
        {isCountryZone(detail) ? (
          <p className="wx-gfz-note-muted wx-gfz-note-muted--left">
            โซนแบบประเทศครอบคลุมแรงงานทุกคนอยู่แล้ว จึงไม่มีรายชื่อมอบหมายรายคน
          </p>
        ) : assignedWorkers.length === 0 ? (
          <p className="wx-gfz-note-muted wx-gfz-note-muted--left">ยังไม่มีแรงงานที่มอบหมายให้ Zone นี้</p>
        ) : (
          <div className="wx-gfz-worker-list">
            {assignedWorkers.map((worker) => (
              <div className="wx-gfz-worker-row" key={worker.workerId}>
                <div className="wx-gfz-worker-row-name">
                  <strong>{worker.workerName ?? "-"}</strong>
                  <span>{worker.workerCode ?? "-"}</span>
                </div>
                <span className="wx-badge wx-badge--success">{worker.assignmentStatus}</span>
                {nextActions.canAssignWorkers && (
                  <button
                    type="button"
                    className="wx-gfz-row-action"
                    disabled={removeWorkerState.status === "submitting" && removeWorkerState.workerId === worker.workerId}
                    onClick={() => onRemoveWorker(worker.workerId)}
                  >
                    ถอดออกจาก Zone
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {removeWorkerState.status === "error" && (
          <p className="wx-gfz-form-message" role="alert">
            {describeWriteError(removeWorkerState.error, "การมอบหมายนี้ถูกถอดไปแล้ว")}
          </p>
        )}
      </section>

      <section className="wx-gfz-dsec">
        <h3>ขั้นตอนถัดไป (Next Actions)</h3>
        <div className="wx-gfz-next-actions">
          <button type="button" className="wx-gfz-row-action wx-gfz-row-action--full" disabled={!nextActions.canEdit} onClick={onEdit}>
            แก้ไข Zone
          </button>
          {/* Assigning workers one at a time to a zone that already covers all
              of them is not a thing an operator can usefully do, so the action
              is absent rather than present and pointless. */}
          {!isCountryZone(detail) && (
            <button type="button" className="wx-gfz-row-action wx-gfz-row-action--full" disabled={!nextActions.canAssignWorkers} onClick={onAssign}>
              มอบหมายแรงงาน
            </button>
          )}
          <button type="button" className="wx-gfz-row-action wx-gfz-row-action--full" disabled={!nextActions.canEvaluate} onClick={onEvaluate}>
            ประเมิน Zone นี้
          </button>
        </div>
        {/* This used to print "canEdit/canAssignWorkers คำนวณจาก role..." - raw
            API field names, at an operator, explaining our own permission
            plumbing. What they actually need to know is why a button in front of
            them is greyed out, said in their own words. */}
        <p className="wx-gfz-note-muted wx-gfz-note-muted--left">
          ปุ่มที่กดไม่ได้ แปลว่าบัญชีของคุณไม่มีสิทธิ์ทำรายการนั้นกับโซนนี้ — แก้ไขและมอบหมายได้เฉพาะผู้ดูแลระบบหรือ Agent เจ้าของโซน ส่วนการประเมินทำได้ทุกคนที่เห็นโซนนี้
        </p>
      </section>
    </div>
  );
}

function CreateZoneView({ titleId, form, onChange, validationError, submitState, onSubmit }) {
  const submitting = submitState.status === "submitting";
  const isCountryProfile = form.zoneType === ZONE_TYPE_COUNTRY;

  return (
    <div className="wx-gfz-drawer">
      <span className="wx-gfz-drawer-kicker">CREATE ZONE</span>
      <h2 id={titleId} className="wx-gfz-drawer-title">
        {isCountryProfile ? "สร้างโซนแบบประเทศ" : "สร้าง Geofence Zone (Circle)"}
      </h2>
      <p className="wx-gfz-drawer-sub">{isCountryProfile ? "โซนระดับประเทศ ครอบคลุมแรงงานทุกคน" : "สร้างโซนใหม่สำหรับนายจ้าง"}</p>

      <div className="wx-gfz-notice wx-gfz-notice--warn">{isCountryProfile ? COUNTRY_PROFILE_SUPERADMIN_NOTICE : CREATE_VIEW_ONLY_NOTICE}</div>

      {/* P161: the profile picker. Two real, different kinds of fence - the
          fields below change with it because they genuinely differ, not to
          hide anything. */}
      <section className="wx-gfz-dsec">
        <h3>รูปแบบโซน (Profile)</h3>
        <label className="wx-gfz-field">
          <span>รูปแบบโซน</span>
          <select aria-label="รูปแบบโซน" value={form.zoneType} onChange={(event) => onChange({ zoneType: event.target.value })}>
            <option value={ZONE_TYPE_CIRCLE}>วงกลมรอบสถานที่ (Circle)</option>
            <option value={ZONE_TYPE_COUNTRY}>ทั้งประเทศ (Country)</option>
          </select>
        </label>
        {isCountryProfile && (
          <>
            <label className="wx-gfz-field">
              <span>
                ประเทศ <b>*</b>
              </span>
              <select aria-label="ประเทศ" value={form.countryCode} onChange={(event) => onChange({ countryCode: event.target.value })}>
                {COUNTRY_ZONE_OPTIONS.map((option) => (
                  <option key={option.countryCode} value={option.countryCode}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="wx-gfz-notice">{COUNTRY_PROFILE_NOTICE}</div>
          </>
        )}
      </section>

      <section className="wx-gfz-dsec">
        <h3>ข้อมูล Zone</h3>
        <label className="wx-gfz-field">
          <span>
            ชื่อโซน <b>*</b>
          </span>
          <input type="text" aria-label="ชื่อโซน" placeholder="เช่น ประตูหลัก คลังสินค้าสำโรง" value={form.zoneName} onChange={(event) => onChange({ zoneName: event.target.value })} />
        </label>
        {/* A country fence belongs to no employer, so the field is absent
            rather than present and ignored. */}
        {!isCountryProfile && (
          <label className="wx-gfz-field">
            <span>
              employerId <span className="wx-gfz-optional">(ไม่บังคับ)</span>
            </span>
            <input type="text" aria-label="รหัสนายจ้าง" placeholder="รหัสนายจ้าง (ไม่บังคับ)" value={form.employerId} onChange={(event) => onChange({ employerId: event.target.value })} />
          </label>
        )}
      </section>

      {/* The circle geometry only exists for a circle. For a country profile
          the outline lives on the server and is not something an operator
          types, so these inputs are absent rather than disabled. */}
      {!isCountryProfile && (
      <section className="wx-gfz-dsec">
        <h3>รูปทรง Circle</h3>
        <div className="wx-gfz-field-row">
          <label className="wx-gfz-field">
            <span>
              ละติจูดจุดกึ่งกลาง <b>*</b>
            </span>
            <input
              type="text"
              inputMode="decimal"
              aria-label="ละติจูดจุดกึ่งกลาง"
              placeholder="เช่น 13.756300"
              value={form.centerLatitude}
              onChange={(event) => onChange({ centerLatitude: event.target.value })}
            />
          </label>
          <label className="wx-gfz-field">
            <span>
              ลองจิจูดจุดกึ่งกลาง <b>*</b>
            </span>
            <input
              type="text"
              inputMode="decimal"
              aria-label="ลองจิจูดจุดกึ่งกลาง"
              placeholder="เช่น 100.501800"
              value={form.centerLongitude}
              onChange={(event) => onChange({ centerLongitude: event.target.value })}
            />
          </label>
        </div>
        <label className="wx-gfz-field">
          <span>
            รัศมี (เมตร) <b>*</b>
          </span>
          <input type="number" aria-label="รัศมี (เมตร)" min="10" max="5000" placeholder="10 - 5000" value={form.radiusMeters} onChange={(event) => onChange({ radiusMeters: event.target.value })} />
        </label>
        <span className="wx-gfz-field-hint">{RADIUS_HINT_TEXT}</span>
      </section>
      )}

      <section className="wx-gfz-dsec">
        <label className="wx-gfz-field">
          <span>
            หมายเหตุ <span className="wx-gfz-optional">(ไม่บังคับ)</span>
          </span>
          <textarea aria-label="note" placeholder="เช่น ประตูหลักฝั่งทางเข้ารถบรรทุก" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
        </label>
      </section>

      {!isCountryProfile && <div className="wx-gfz-notice">{NO_POLYGON_FIELD_NOTICE}</div>}

      {validationError && (
        <p className="wx-gfz-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-gfz-form-message" role="alert">
          {describeWriteError(submitState.error, "Zone นี้ถูกแก้ไขไปแล้ว กรุณาลองใหม่อีกครั้ง")}
        </p>
      )}

      <div className="wx-gfz-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-gfz-drawer-actions-full" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังบันทึก..." : "บันทึก Zone"}
        </button>
      </div>
    </div>
  );
}

function EditZoneView({ titleId, zoneCode, form, onChange, validationError, submitState, onSubmit, onBack }) {
  const submitting = submitState.status === "submitting";

  return (
    <div className="wx-gfz-drawer">
      <span className="wx-gfz-drawer-kicker">EDIT ZONE</span>
      <h2 id={titleId} className="wx-gfz-drawer-title">
        แก้ไข Geofence Zone
      </h2>
      <p className="wx-gfz-drawer-sub">{zoneCode ?? "-"}</p>

      <div className="wx-gfz-notice wx-gfz-notice--warn">{CREATE_VIEW_ONLY_NOTICE}</div>

      <section className="wx-gfz-dsec">
        <h3>ข้อมูล Zone</h3>
        <label className="wx-gfz-field">
          <span>
            ชื่อโซน <b>*</b>
          </span>
          <input type="text" aria-label="ชื่อโซน" value={form.zoneName} onChange={(event) => onChange({ zoneName: event.target.value })} />
        </label>
        <label className="wx-gfz-field">
          <span>สถานะโซน</span>
          <select aria-label="สถานะโซน" value={form.zoneStatus} onChange={(event) => onChange({ zoneStatus: event.target.value })}>
            {ZONE_STATUS_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="wx-gfz-dsec">
        <h3>รูปทรง Circle</h3>
        <div className="wx-gfz-field-row">
          <label className="wx-gfz-field">
            <span>
              ละติจูดจุดกึ่งกลาง <b>*</b>
            </span>
            <input type="text" inputMode="decimal" aria-label="ละติจูดจุดกึ่งกลาง" value={form.centerLatitude} onChange={(event) => onChange({ centerLatitude: event.target.value })} />
          </label>
          <label className="wx-gfz-field">
            <span>
              ลองจิจูดจุดกึ่งกลาง <b>*</b>
            </span>
            <input type="text" inputMode="decimal" aria-label="ลองจิจูดจุดกึ่งกลาง" value={form.centerLongitude} onChange={(event) => onChange({ centerLongitude: event.target.value })} />
          </label>
        </div>
        <label className="wx-gfz-field">
          <span>
            รัศมี (เมตร) <b>*</b>
          </span>
          <input type="number" aria-label="รัศมี (เมตร)" min="10" max="5000" value={form.radiusMeters} onChange={(event) => onChange({ radiusMeters: event.target.value })} />
        </label>
        <span className="wx-gfz-field-hint">{RADIUS_HINT_TEXT}</span>
        <label className="wx-gfz-field">
          <span>
            หมายเหตุ <span className="wx-gfz-optional">(ไม่บังคับ)</span>
          </span>
          <textarea aria-label="note" value={form.note} onChange={(event) => onChange({ note: event.target.value })} />
        </label>
      </section>

      <div className="wx-gfz-notice">{NO_POLYGON_FIELD_NOTICE}</div>

      {validationError && (
        <p className="wx-gfz-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-gfz-form-message" role="alert">
          {describeWriteError(submitState.error, "Zone นี้ถูกแก้ไขไปแล้วก่อนหน้านี้ กรุณารีเฟรชแล้วลองใหม่")}
        </p>
      )}

      <div className="wx-gfz-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack} disabled={submitting}>
          ย้อนกลับ
        </button>
        <button type="button" className="wx-button wx-button--primary" onClick={onSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "บันทึกการแก้ไข"}
        </button>
      </div>
    </div>
  );
}

function AssignWorkersView({ titleId, zoneCode, workerIdsInput, onWorkerIdsChange, assignmentNote, onAssignmentNoteChange, validationError, submitState, onSubmit }) {
  const submitting = submitState.status === "submitting";
  const result = submitState.result;

  return (
    <div className="wx-gfz-drawer">
      <span className="wx-gfz-drawer-kicker">ASSIGN WORKERS</span>
      <h2 id={titleId} className="wx-gfz-drawer-title">
        มอบหมายแรงงานเข้า Zone
      </h2>
      <p className="wx-gfz-drawer-sub">{zoneCode ?? "-"}</p>

      <section className="wx-gfz-dsec">
        <h3>รายชื่อแรงงาน (workerIds)</h3>
        <label className="wx-gfz-field">
          <span>
            รายการรหัสแรงงาน <b>*</b>
          </span>
          <textarea
            aria-label="รายการรหัสแรงงาน"
            placeholder="ระบุรหัสแรงงาน คั่นด้วยจุลภาคหรือขึ้นบรรทัดใหม่"
            value={workerIdsInput}
            onChange={(event) => onWorkerIdsChange(event.target.value)}
          />
        </label>
        <span className="wx-gfz-field-hint">คั่นรหัสแรงงานด้วยจุลภาคหรือขึ้นบรรทัดใหม่ สูงสุด {MAX_WORKER_ASSIGNMENT_BATCH} รายการต่อคำขอ</span>
      </section>

      <section className="wx-gfz-dsec">
        <h3>หมายเหตุการมอบหมาย</h3>
        <label className="wx-gfz-field">
          <span>
            assignmentNote <span className="wx-gfz-optional">(ไม่บังคับ)</span>
          </span>
          <textarea aria-label="assignmentNote" placeholder="เช่น มอบหมายรอบตรวจสอบประจำเดือน" value={assignmentNote} onChange={(event) => onAssignmentNoteChange(event.target.value)} />
        </label>
      </section>

      <div className="wx-gfz-notice">
        {ASSIGN_NOTICE_LINES.map((line) => (
          <span key={line} className="wx-gfz-notice-line">
            {line}
          </span>
        ))}
      </div>
      <div className="wx-gfz-notice wx-gfz-notice--warn">{ASSIGN_NO_NOTIFICATION_NOTICE}</div>

      {validationError && (
        <p className="wx-gfz-form-message" role="alert">
          {validationError}
        </p>
      )}
      {submitState.status === "error" && (
        <p className="wx-gfz-form-message" role="alert">
          {describeWriteError(submitState.error, "ไม่สามารถมอบหมายแรงงานได้ในขณะนี้")}
        </p>
      )}

      {result && (
        <section className="wx-gfz-dsec">
          <h3>ผลลัพธ์การมอบหมาย</h3>
          <div className="wx-gfz-kv-row">
            <span>ที่ขอไป</span>
            <strong>{result.requestedCount}</strong>
          </div>
          <div className="wx-gfz-kv-row">
            <span>เพิ่มเข้าโซน</span>
            <strong>{result.assignedCount}</strong>
          </div>
          <div className="wx-gfz-kv-row">
            <span>อยู่ในโซนอยู่แล้ว</span>
            <strong>{result.alreadyAssignedCount}</strong>
          </div>
          <div className="wx-gfz-kv-row">
            <span>ไม่ผ่าน</span>
            <strong>{result.rejectedCount}</strong>
          </div>
          {(result.rejectedWorkers ?? []).length > 0 && (
            <div className="wx-gfz-worker-list" style={{ marginTop: "8px" }}>
              {result.rejectedWorkers.map((rejected) => (
                <div className="wx-gfz-worker-row" key={rejected.workerId}>
                  <div className="wx-gfz-worker-row-name">
                    <strong>{rejected.workerId}</strong>
                  </div>
                  <span className="wx-badge wx-badge--danger">{rejected.reason}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <div className="wx-gfz-drawer-actions">
        <button type="button" className="wx-button wx-button--primary wx-gfz-drawer-actions-full" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังมอบหมาย..." : "มอบหมายแรงงาน"}
        </button>
      </div>
    </div>
  );
}

function EvaluateView({ titleId, workerIdInput, onWorkerIdChange, zoneIdInput, onZoneIdChange, dryRun, onDryRunChange, submitState, onSubmit, onOpenOutOfZone }) {
  const submitting = submitState.status === "submitting";
  const result = submitState.result;

  return (
    <div className="wx-gfz-drawer">
      <span className="wx-gfz-drawer-kicker">MANUAL EVALUATE</span>
      <h2 id={titleId} className="wx-gfz-drawer-title">
        ประเมิน Zone (Manual Evaluate)
      </h2>
      <p className="wx-gfz-drawer-sub">ตรวจว่าแรงงานอยู่ในโซนหรือนอกโซน</p>

      <section className="wx-gfz-dsec">
        <h3>พารามิเตอร์</h3>
        <label className="wx-gfz-field">
          <span>
            workerId <span className="wx-gfz-optional">(ไม่บังคับ)</span>
          </span>
          <input
            type="text"
            aria-label="รหัสแรงงาน"
            placeholder="ว่าง = ทุกแรงงานในสิทธิ์"
            value={workerIdInput}
            onChange={(event) => onWorkerIdChange(event.target.value)}
          />
        </label>
        <label className="wx-gfz-field">
          <span>
            zoneId <span className="wx-gfz-optional">(ไม่บังคับ)</span>
          </span>
          <input
            type="text"
            aria-label="zoneId"
            placeholder="ว่าง = ทุก zone ที่มองเห็น"
            value={zoneIdInput}
            onChange={(event) => onZoneIdChange(event.target.value)}
          />
        </label>
        <label className="wx-gfz-field wx-gfz-toggle-field">
          <span>โหมดทดลอง</span>
          <span className="wx-gfz-toggle">
            <input type="checkbox" aria-label="โหมดทดลอง" checked={dryRun} onChange={(event) => onDryRunChange(event.target.checked)} />
            <span className="wx-gfz-toggle-track">
              <span className="wx-gfz-toggle-thumb" />
            </span>
          </span>
        </label>
        <span className="wx-gfz-field-hint">{EVALUATE_DRYRUN_WARNING}</span>
        <button type="button" className="wx-button wx-button--primary wx-gfz-run-evaluate-button" disabled={submitting} onClick={onSubmit}>
          {submitting ? "กำลังประเมิน..." : "เริ่มการประเมิน"}
        </button>
      </section>

      <div className="wx-gfz-notice wx-gfz-notice--warn">{EVALUATE_SOURCE_WARNING}</div>

      {submitState.status === "error" && (
        <p className="wx-gfz-form-message" role="alert">
          {describeEvaluateError(submitState.error)}
        </p>
      )}

      {result && (
        <>
          <section className="wx-gfz-dsec">
            <h3>ผลสรุป</h3>
            <div className="wx-gfz-summary-grid">
              <div className="wx-gfz-summary-cell">
                <span>จำนวนแรงงานที่ตรวจ</span>
                <strong>{result.evaluatedWorkerCount}</strong>
              </div>
              <div className="wx-gfz-summary-cell">
                <span>อยู่ในโซน</span>
                <strong>{result.insideCount}</strong>
              </div>
              <div className="wx-gfz-summary-cell">
                <span>อยู่นอกโซน</span>
                <strong>{result.outsideCount}</strong>
              </div>
              <div className="wx-gfz-summary-cell">
                <span>ไม่มีพิกัด</span>
                <strong>{result.noLocationCount}</strong>
              </div>
              <div className="wx-gfz-summary-cell">
                <span>สร้างรายการผิดปกติ</span>
                <strong>{result.createdAbnormalCount}</strong>
              </div>
              <div className="wx-gfz-summary-cell">
                <span>ข้าม (มีรายการค้างอยู่แล้ว)</span>
                <strong>{result.skippedOpenDuplicateCount}</strong>
              </div>
            </div>
            <div className="wx-gfz-notice">ระบบยังไม่แจ้งให้อัตโนมัติ กรุณาติดต่อแรงงานหรือหัวหน้างานเอง</div>
          </section>

          <section className="wx-gfz-dsec">
            <h3>ผลลัพธ์รายแรงงาน (results)</h3>
            <div className="wx-gfz-result-list">
              {(result.results ?? []).map((row, index) => (
                <div
                  className={`wx-gfz-result-row${row.evaluationStatus === "Outside" ? " wx-gfz-result-row--clickable" : ""}`}
                  key={`${row.workerId}-${row.zoneId ?? "none"}`}
                  onClick={row.evaluationStatus === "Outside" ? () => onOpenOutOfZone(index) : undefined}
                >
                  <div className="wx-gfz-result-row-top">
                    <strong>
                      {row.workerCode ?? row.workerId} {row.zoneName ? `· ${row.zoneName}` : ""}
                    </strong>
                    <span className={`wx-badge wx-badge--${evalStatusBadgeVariant(row.evaluationStatus)}`}>{row.evaluationStatus}</span>
                  </div>
                  <div className="wx-gfz-result-row-meta">
                    <div>
                      <span>ระยะห่าง (เมตร)</span>
                      <strong>{row.distanceMeters ?? "-"}</strong>
                    </div>
                    <div>
                      <span>รัศมี (เมตร)</span>
                      <strong>{row.radiusMeters ?? "-"}</strong>
                    </div>
                  </div>
                  {row.evaluationStatus === "Outside" && (
                    <button type="button" className="wx-gfz-row-action wx-gfz-row-action--full" onClick={() => onOpenOutOfZone(index)}>
                      ดูผล OutOfZone
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function OutOfZoneResultView({ titleId, evaluateResponse, item, onBack }) {
  if (!item) {
    return null;
  }

  return (
    <div className="wx-gfz-drawer">
      <span className="wx-gfz-drawer-kicker">OUTOFZONE RESULT</span>
      <h2 id={titleId} className="wx-gfz-drawer-title">
        ผลลัพธ์ Outside
      </h2>
      <p className="wx-gfz-drawer-sub">
        {item.workerCode ?? item.workerId} · {item.zoneName ?? "-"}
      </p>
      <div className="wx-gfz-drawer-status">
        <span className="wx-badge wx-badge--danger">Outside</span>
        <span className="wx-badge wx-badge--neutral">{item.zoneId ?? "-"}</span>
      </div>

      <section className="wx-gfz-dsec">
        <h3>รายละเอียดผลประเมิน</h3>
        <div className="wx-gfz-kv-row">
          <span>รหัสแรงงาน</span>
          <strong>{item.workerCode ?? item.workerId}</strong>
        </div>
        <div className="wx-gfz-kv-row">
          <span>ชื่อโซน</span>
          <strong>{item.zoneName ?? "-"}</strong>
        </div>
        <div className="wx-gfz-kv-row">
          <span>ระยะห่าง (เมตร)</span>
          <strong>{item.distanceMeters ?? "-"}</strong>
        </div>
        <div className="wx-gfz-kv-row">
          <span>รัศมี (เมตร)</span>
          <strong>{item.radiusMeters ?? "-"}</strong>
        </div>
        <div className="wx-gfz-kv-row">
          <span>สถานะการตรวจ</span>
          <strong>
            <span className="wx-badge wx-badge--danger">Outside</span>
          </strong>
        </div>
      </section>

      <section className="wx-gfz-dsec">
        <h3>สถานะการสร้าง OutOfZone Abnormal</h3>
        <div className="wx-gfz-abnormal-indicator wx-gfz-abnormal-indicator--created">
          createdAbnormalCount (ทั้งคำขอ): {evaluateResponse.createdAbnormalCount} — {evaluateResponse.dryRun ? "dryRun เปิดอยู่ จึงยังไม่มีการสร้างจริง" : "เมื่อรันด้วย dryRun = false"}
        </div>
        <div className="wx-gfz-abnormal-indicator wx-gfz-abnormal-indicator--skipped">
          ข้ามไปแล้วทั้งคำขอ: {evaluateResponse.skippedOpenDuplicateCount} — แรงงานที่มีรายการออกนอกโซนค้างอยู่ ระบบจะไม่บันทึกซ้ำ
        </div>
        <div className="wx-gfz-abnormal-indicator">ระบบยังไม่แจ้งให้อัตโนมัติ กรุณาติดต่อแรงงานหรือหัวหน้างานเอง</div>
        <p className="wx-gfz-note-muted wx-gfz-note-muted--left">{NO_FAKE_LOCATION_SCORE_NOTE} และไม่มีข้อมูลชีวมิติหรือใบหน้าในผลลัพธ์นี้</p>
      </section>

      <div className="wx-gfz-drawer-actions">
        <button type="button" className="wx-button wx-button--ghost" onClick={onBack}>
          ย้อนกลับ
        </button>
      </div>
    </div>
  );
}
